// Renders a chat message's markdown source to display HTML. Used by
// `ChatMsg` at render time — pulling this out of
// `ChatService.buildMessage` (where it used to run on every receive) means:
//
//   - Storage only holds the raw user input, not a render artifact.
//     Markdown renderer changes apply automatically to old messages
//     instead of being frozen at receive-time output. Migrating to a
//     different markdown lib later doesn't need a data migration.
//   - Total compute scales with messages *viewed*, not messages
//     *received*. A user who receives 5,000 messages but scrolls through
//     300 of them pays for 300 renders, not 5,000.
//   - Storage shrinks (no HTML duplicating the source).
//
// Cache keyed on the source string. Bounded by the message store's
// per-room cap × room count, so leak risk is negligible in practice
// (~200 entries per active room). The cache is module-scoped so multiple
// `ChatMsg` instances rendering the same message — including
// across viewport recycling under CDK virtual scroll — share work.
//
// Returns the raw HTML string; the calling component wraps it with
// `DomSanitizer.bypassSecurityTrustHtml` (so the sanitizer dependency
// stays inside the Angular context, not in this pure helper).
import { buildFormatTreeFromString } from './markdown';

const cache = new Map<string, string>();

export function renderMessageHtml(source: string): string {
  if (!source) return '';
  const cached = cache.get(source);
  if (cached !== undefined) return cached;
  const html = buildFormatTreeFromString(
    source.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
  ).assembleString({
    enableSyntaxHighlighting: true,
    setInteractive: true,
    codeblockCopy: true,
  });
  cache.set(source, html);
  return html;
}
